import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * FindScene — the "Find scene" search bar for TouchTour Studio (WP-K).
 *
 * A compact search bar that lives in the toolbar (right of the Tidy Board
 * segment) plus a ⌘F / Ctrl+F shortcut that focuses it. Typing filters the
 * tour's board nodes by name (case-insensitive) and the matches drop down
 * beneath the bar; clicking one (or pressing Enter) flies the camera there.
 *
 * It never reaches into TourCanvas internals: selecting a result eases the
 * camera via
 * api.jumpTo (which runs through animateTo) and highlights the node by raising
 * it through onSelectImage (onUpdateActiveImage).
 *
 * Keyboard: ⌘F/Ctrl+F focuses the bar (overriding the browser's native find),
 * Up/Down move the highlighted result, Enter selects it, Esc clears/closes.
 */

const FindScene = ({ images, canvasApiRef, onSelectImage }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const nodes = useMemo(() => Object.values(images ?? {}), [images]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    const sorted = [...nodes].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? ""),
    );
    if (term === "") return sorted;
    return sorted.filter(node => (node.name ?? "").toLowerCase().includes(term));
  }, [nodes, query]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(0);
  }, []);

  const selectNode = useCallback(
    node => {
      if (!node) return;
      const api = canvasApiRef && canvasApiRef.current;
      if (api && typeof api.jumpTo === "function") {
        api.jumpTo(node.x, node.y);
      }
      if (onSelectImage) onSelectImage(node.id);
      setQuery("");
      close();
      if (inputRef.current) inputRef.current.blur();
    },
    [canvasApiRef, onSelectImage, close],
  );

  // Global ⌘F / Ctrl+F focuses the bar, overriding the browser's native find.
  useEffect(() => {
    const onKeyDown = event => {
      const isFind = (event.metaKey || event.ctrlKey) && event.key === "f";
      if (!isFind) return;
      event.preventDefault();
      setOpen(true);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close the dropdown when clicking outside the search bar.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, close]);

  // Keep the highlighted row in range as the result set shrinks/grows.
  useEffect(() => {
    setActiveIndex(index =>
      results.length === 0 ? 0 : Math.min(index, results.length - 1),
    );
  }, [results]);

  const handleKeyDown = event => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (query !== "") {
        setQuery("");
      } else {
        close();
        if (inputRef.current) inputRef.current.blur();
      }
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index =>
        results.length === 0 ? 0 : (index + 1) % results.length,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index =>
        results.length === 0
          ? 0
          : (index - 1 + results.length) % results.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      selectNode(results[activeIndex]);
    }
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.searchRow}>
        <FontAwesomeIcon
          icon={["fal", "magnifying-glass"]}
          className={classes.searchIcon}
        />
        <input
          ref={inputRef}
          type="text"
          className={classes.input}
          placeholder="Find scene…"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <span className={classes.hint}>⌘F</span>
      </div>

      {open && (
        <div className={classes.results}>
          {results.length === 0 ? (
            <div className={classes.empty}>No scenes found</div>
          ) : (
            results.map((node, index) => (
              <button
                key={node.id}
                type="button"
                className={
                  index === activeIndex
                    ? `${classes.result} ${classes.resultActive}`
                    : classes.result
                }
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectNode(node)}
              >
                <FontAwesomeIcon
                  icon={["fal", "location-dot"]}
                  className={classes.resultIcon}
                />
                <span className={classes.resultName}>
                  {node.name ?? "Untitled scene"}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  // Sits inline in the toolbar, just right of the Tidy Board segment. The
  // dropdown is absolutely positioned against this relative container.
  container: {
    position: "relative",
    marginLeft: theme.spacing2,
    width: 180,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing2,
    height: 32,
    padding: [0, 10],
    backgroundColor: theme.colorBackground,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius2,
    boxSizing: "border-box",
  },
  searchIcon: {
    color: theme.colorGrey,
    fontSize: 13,
  },
  input: {
    flex: "1 1 auto",
    minWidth: 0,
    background: "transparent",
    border: "none",
    outline: "none",
    color: theme.textColor,
    fontSize: 13,
    fontFamily: theme.fontFamily,
    "&::placeholder": {
      color: theme.colorGrey,
    },
  },
  hint: {
    flex: "0 0 auto",
    color: theme.colorGrey,
    fontSize: 10,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    padding: [1, 5],
  },
  results: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    width: 260,
    maxHeight: "50vh",
    overflowY: "auto",
    zIndex: 1000,
    backgroundColor: theme.colorBackground,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius2,
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5)",
    padding: theme.spacing1,
  },
  empty: {
    color: theme.colorGrey,
    fontSize: 14,
    textAlign: "center",
    padding: theme.spacing3,
  },
  result: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing2,
    width: "100%",
    padding: [theme.spacing2, theme.spacing2],
    background: "transparent",
    border: "none",
    borderRadius: theme.borderRadius,
    color: theme.textColor,
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left",
  },
  resultActive: {
    backgroundColor: theme.colorPrimary,
  },
  resultIcon: {
    color: theme.colorGrey,
    fontSize: 14,
    flex: "0 0 auto",
  },
  resultName: {
    flex: "1 1 auto",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

export default FindScene;
